define('playback',['doneable', 'edapUnderscore'], function(Doneable, _) {
    'use strict';

    function Playback(edap) {
        var playback = this,
            eventCounter = 0,
            isDisabled = false,
            maxLogEvents = 200,
            defaultEventLog = {
                id: null,
                eventName: null,
                data: null,
                executedListeners: []
            };


        /**
         *
         * Internal Methods
         *
         */


       /**
        *  Object contains the mark name -> event ID mapping
        */
        playback.marks = {};


       /**
        *  Array with the sequence of event triggers and their associated data
        */
        playback.eventLog = [];


       /**
        *  Gets the next unique incrementing event ID
        *
        *  @private
        *  @returns {Integer} - The next unique incrementing event ID
        */
        playback.getNextEventID = function() {
            // Post-Incrementing meaning it will increment after it returns the value
            // ie. first time it runs it will return 0
            return eventCounter++;
        };


       /**
        *  Creates a new Event instance.
        *
        *  @private
        *  @returns {Object} - new Event instance
        */
        playback.createEventInstance = function() {
            var newLog = _.clone(defaultEventLog, true);
            newLog.id = playback.getNextEventID();
            return newLog;
        };


       /**
        *  Gets the eventLog index position for the event ID associated with a mark.
        *
        *  @private
        *  @throws {Error} - If the proper index cannot be determined
        *  @param {String} [markName] - Name of the mark to find the correct index
        *  @returns {Array} - [index position in eventLog, number of missed events]
        */
        playback.getIndexOfMark = function(markName) {
            var logName = 'getIndexOfMark',
                eventLog = playback.getEventLog(),
                eventID = playback.marks[markName],
                index,
                missedEvents,
                err,
                i,
                len,
                ret;

            if (typeof eventID !== 'number') {
                err = new Error(logName + '(): invalid mark name ' + markName);
                edap.error(err);
                throw err;
            }

            // If we haven't hit the maxLogEvents and started shift()'ing off old events then eventID
            // should match up with the eventsLog index position.
            if (eventLog[eventID] && eventLog[eventID].id === eventID) {
                index = eventID;
                ret = [index, 0];

            // If we have hit the max and started dropping old events then we will search the eventLog
            // for the correct position that correlates to eventID value.
            } else {
                // If the oldest event in the log is more recent than the mark event ID then we know we
                // are going to miss some events. In that case we should just return 0 as the start index
                // and the number of missed events.
                if (eventLog[0].id > eventID) {
                    missedEvents = eventLog[0].id - eventID;
                    index = 0;
                    return [index, missedEvents];
                }

                // Otherwise we just search for the eventID in the log.
                for (i = 0, len = eventLog.length; i < len; i++) {
                    if (eventLog[i].id === eventID) {
                        index = i;
                        return [index, 0];
                    }
                }

                // If we can't find it or make sense of where it could be then we are going to throw an
                // error since we don't know how to play back events that we can't line up.
                err = new Error(logName + '(): could not find eventID=' + eventID);
                edap.error(err);
                throw err;
            }

            return ret;
        };


        /**
         *
         * Public Methods
         *
         */


       /**
        *  Gets the Event Log array
        *
        *  @public
        *  @returns {Array} - Event Log array
        */
        playback.getEventLog = function() {
            return playback.eventLog;
        };


       /**
        *  Sets the maximum number of log entries that will be stored. This is a circular buffer so
        *  the oldest entries will be flushed out to make room for newer entries. If the buffer is
        *  lowered below the current number of entries then this function will also flush out the oldest
        *  entries until it is at the new maximum number.
        *
        *  @public
        *  @throws {TypeError} - if parameter validation fails
        *  @param {Integer} [num] - New maximum number of entries to set, must be >= 0
        */
        playback.setMaxLogEvents = function(num) {
            var logName = 'setMaxLogEvents',
                eventLog,
                err,
                i,
                max;

            if (typeof num !== 'number' || num < 0) {
                err = new TypeError(logName + '(): must be passed a positive integer or 0');
                edap.error(err);
                throw err;
            }


            // set the new max event log
            maxLogEvents = num;
            eventLog = playback.getEventLog();

            // Reduce the size of the event log if the max is greater than the current log size
            for (i = eventLog.length, max = maxLogEvents; i > max; i--) {
                eventLog.shift();
            }
        };


       /**
        *  Resets the marks, eventLog, and event ID.
        *
        *  @public
        */
        playback.reset = function() {
            playback.eventLog = [];
            playback.marks = {};
            eventCounter = 0;
        };


       /**
        *  Adds a new event and its associated data into the eventLog.
        *
        *  @public
        *  @param {String} [event] - Name of the event that was trigger()'d
        *  @param {String} [data] - Extended data that was passed to each listener for this event
        *  @returns {Object} - Event object or null if the Playback system is disabled.
        *                      When an Event object is returned it will have the following keys:
        *                          id - The incrementing event ID
        *                          eventName - name of the event
        *                          data - extended data passed to each listener
        *                          executedListeners - Array of listener UUID's that have been executed
        *                                              with this event
        */
        playback.addEvent = function(event, data) {
            var newLog,
                eventLog;

            // If this is disabled then we bail
            if (isDisabled) {
                return null;
            }

            newLog = playback.createEventInstance();
            eventLog = playback.getEventLog();
            newLog.eventName = event;
            newLog.data = data;

            eventLog.push(newLog);

            // Make sure we honor the maxLogEvents size. The assumption is that setMaxLogEvents will prune
            // an eventLog to size so that if we are greater than the max size here then it is at most by 1.
            if (eventLog.length > maxLogEvents) {
                eventLog.shift();
            }

            return newLog;
        };


       /**
        *  Creates a named mark at the current event ID. This is used in conjunction with replay()
        *  so that you can replay the events from the mark to the most current event. Primarily used
        *  when code is being loaded async and you want to have that code process messages that were
        *  triggered before the code was loaded. Named marks must have unique names.
        *
        *  @public
        *  @param {String} [markName] - The name of the mark
        */
        playback.setMark = function(markName) {
            var logName = 'setMark',
                err;

            if (typeof markName !== 'string') {
                err = new TypeError(logName + '(): markName must be a string');
                edap.error(err);
                throw err;
            }

            if (playback.marks.hasOwnProperty(markName)) {
                err = new TypeError(logName + '(): ' + markName + ' already used');
                edap.error(err);
                throw err;
            }

            playback.marks[markName] = eventCounter;
        };


       /**
        *  Removes a mark from the playback system.
        *
        *  @public
        *  @throws {TypeError} - if parameter validation fails
        *  @param {String} [markName] - The name of the mark
        *  @returns {Boolean} - true if markName existed and was removed, otherwise false
        */
        playback.removeMark = function(markName) {
            var err;

            if (typeof markName !== 'string') {
                err = new TypeError('removeMark(): markName must be a string');
                edap.error(err);
                throw err;
            }

            if (playback.marks.hasOwnProperty(markName)) {
                delete playback.marks[markName];
                return true;
            }

            return false;
        };


       /**
        *  Replays events from the eventLog.
        *
        *  @public
        *  @throws {TypeError} - if parameter validation fails
        *  @param {String} [optMarkName] - Name of the mark to start replaying from. Defaults to 'init'.
        *  @param {Object} [triggerOpts] - Options to pass into each trigger() call for
        *                                  each message that is being replayed.
        *  @returns {Doneable} - Resolves when all the callbacks for the event are done
        */
        playback.replay = function(optMarkName, triggerOpts) {
            var logName = 'replay',
                triggerDoneables = [],
                triggerDone,
                eventLog,
                clonedEventLog,
                indexInfo,
                startIndex,
                missedEvents,
                i,
                len,
                err;

            // defaults to the "init" mark if none is passed
            optMarkName = optMarkName || 'init';
            triggerOpts = triggerOpts || {};

            if (!playback.marks.hasOwnProperty(optMarkName)) {
                err = new TypeError(logName + '(): cannot find markName=' + optMarkName);
                edap.error(err);
                throw err;
            }

            eventLog = playback.getEventLog();
            indexInfo = playback.getIndexOfMark(optMarkName);

            // Decode info into more readable variable names
            startIndex = indexInfo[0];
            missedEvents = indexInfo[1];

            // If we have missed some events then log it
            if (missedEvents > 0) {
                edap.error(logName + '(): missed ' + indexInfo[1] + ' events in replay');
            }

            // Get a non-deep clone so that we preserve the original list, but get the references
            // to the Event objects stored in each index position.
            clonedEventLog = _.clone(eventLog);

            for (i = startIndex, len = clonedEventLog.length; i < len; i++) {
                // For each event we want to pass in the event info and data. The "forceData" flag is set so that
                // this trigger won't extend the current ADL over the data, it will just use the data as is. The
                // "playbackObj" is passed so that trigger won't execute any previously executed listeners and so
                // it can update the EventLog entry with any new listeners that get executed.
                triggerDone = edap.trigger(clonedEventLog[i].eventName, clonedEventLog[i].data, _.defaults(
                    {},
                    triggerOpts,
                    {
                        forceData: true,
                        playbackObj: clonedEventLog[i]
                    })
                );
                triggerDoneables.push(triggerDone);
            }

            // We are providing a Doneable return value so that it is possible to wait until all events that
            // need to be replayed are complete.
            return Doneable.all(triggerDoneables);
        };


       /**
        *  Enables the Playback system to log events into the eventLog for replay.
        *
        *  @public
        */
        playback.enable = function() {
            isDisabled = false;
        };


       /**
        *  Disables logging of events into the eventLog. This only disables the logging, it does not
        *  clear any existing logs. If you want to clear existing data use playback.reset().
        *
        *  @public
        */
        playback.disable = function() {
            isDisabled = true;
        };


       /**
        *  Helper to check if the playback system is logging events to the eventLog.
        *
        *  @public
        *  @returns {Boolean} - if playback logging is disabled then it returs true, otherwise false
        */
        playback.isDisabled = function() {
            return isDisabled;
        };

        return playback;
    }

    return Playback;
});

